import {
  canCreateMlSheet,
  canManualAttendance,
  hasAttendanceAccess,
  hasLocationHubBookingAccess,
  hubAllowedPaymentModes,
} from './networkMode.js';
import { altLabel, modLabel } from './keyboard.js';
import { formatNavGoChord } from './sidebarMenu.js';

const SEARCH_ROLES = ['System Admin', 'Admin', 'Accountant', 'Agent', 'Hub Admin'];
const DELIVER_ROLES = [
  'System Admin',
  'Admin',
  'Accountant',
  'Agent',
  'Hub Admin',
  'Contract Party Admin',
];
const SETTINGS_ROLES = ['Admin', 'System Admin'];

export const canUseShipmentSearchShortcut = (user) =>
  SEARCH_ROLES.includes(user?.role);

export const canUseDeliverShortcut = (user) => DELIVER_ROLES.includes(user?.role);

export const canUseCompanySettingsShortcut = (user) => SETTINGS_ROLES.includes(user?.role);

export const isCompanySettingsPath = (pathname) =>
  String(pathname || '').startsWith('/company-settings');

/**
 * Role- and page-aware shortcut list for the help overlay.
 * Only actions the current user can actually run are included.
 */
export function getShortcutGroups({ user, pathname, fullGuide = false } = {}) {
  const mod = modLabel();
  const alt = altLabel();
  const groups = [
    {
      id: 'general',
      title: 'General',
      items: [
        { keys: '?', action: 'Show or hide the shortcut overlay', description: 'Works from most screens when you are not typing.' },
        { keys: 'Esc', action: 'Close dialogs and shortcut help', description: 'Closes the jump list, shortcut overlay, and most modals.' },
        { keys: formatNavGoChord(' '), action: 'Jump to any page you can access', description: 'After A, press Space. Only pages for your role appear.' },
        { keys: formatNavGoChord('h'), action: 'Go to Dashboard', description: 'Opens the dashboard for your role.' },
      ],
    },
  ];

  if (canUseShipmentSearchShortcut(user)) {
    groups[0].items.push({
      keys: '/',
      action: 'Focus shipment search',
      description: 'Jumps to the GC / mobile search in the header.',
    });
  }

  if (canUseDeliverShortcut(user)) {
    groups.push({
      id: 'delivery',
      title: 'Delivery',
      items: [{
        keys: `${mod}+X`,
        action: 'Deliver GC (selected, viewed, or enter ID)',
        description: 'On Update Status with rows selected, marks them delivered. On a shipment view, opens delivery for that GC. Otherwise asks for a GC number.',
      }],
    });
  }

  if (canCreateMlSheet(user)) {
    groups.push({
      id: 'manifest',
      title: 'Manifest',
      items: [{
        keys: `${mod}+D`,
        action: 'Create a new ML sheet',
        description: 'Opens Add ML sheet. Pickup-man agents cannot use this.',
      }],
    });
  }

  if (hasLocationHubBookingAccess(user)) {
    const allowed = hubAllowedPaymentModes(user);
    const items = [];
    if (allowed.includes('paid')) {
      items.push({
        keys: 'F7',
        action: 'New paid booking',
        description: 'Opens Add Shipment with payment mode Paid, if this hub allows Paid.',
      });
    }
    if (allowed.includes('to_pay')) {
      items.push({
        keys: 'F8',
        action: 'New to-pay booking',
        description: 'Opens Add Shipment with payment mode To Pay, if this hub allows To Pay.',
      });
    }
    if (allowed.includes('credit')) {
      items.push({
        keys: 'F9',
        action: 'New credit booking',
        description: 'Opens Add Shipment with payment mode Credit, if this hub allows Credit.',
      });
    }
    items.push({
      keys: `${alt}+M`,
      action: 'New booking with manual GC',
      description: 'Opens Add Shipment with a manual GC number.',
    });
    if (items.length) {
      groups.push({ id: 'booking', title: 'Booking', items });
    }
  }

  if (hasAttendanceAccess(user) && canManualAttendance(user)) {
    groups.push({
      id: 'attendance',
      title: 'Attendance',
      items: [{
        keys: `${mod}+Shift+L`,
        action: 'Clock in or clock out',
        description: 'Works only when attendance is Manual. QR attendance does not use this key — scan QR to clock in.',
      }],
    });
  }

  if (canUseCompanySettingsShortcut(user) && (fullGuide || isCompanySettingsPath(pathname))) {
    groups.push({
      id: 'settings',
      title: 'Company Settings',
      items: [
        { keys: `${mod}+S`, action: 'Save all settings', description: 'Saves every Company Settings category at once. Works while a field is focused.' },
        { keys: '1–5', action: 'Open a category tab', description: 'On Company Settings, jumps to Finance, Booking, Manifest, Documents, or HR.' },
        { keys: '[ or ]', action: 'Previous / next category', description: 'Moves between Company Settings tabs.' },
        { keys: 'J / K', action: 'Next / previous setting card', description: 'Moves focus between setting cards on Company Settings.' },
        { keys: 'Enter', action: 'Expand or collapse the focused card', description: 'Opens extra fields for the focused Company Settings card.' },
        { keys: 'Space', action: 'Toggle the focused setting', description: 'Turns the focused Company Settings switch on or off.' },
      ],
    });
  }

  return groups;
}
